import { useState, useEffect, useRef, useCallback, Fragment } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  MagnifyingGlassIcon,
  RocketLaunchIcon,
  HomeIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
  UsersIcon,
  ClipboardDocumentCheckIcon,
  DocumentChartBarIcon,
  ArrowPathIcon,
  ShieldExclamationIcon,
  InformationCircleIcon,
  WrenchScrewdriverIcon,
  QuestionMarkCircleIcon,
  DocumentTextIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  XMarkIcon,
  Bars3BottomLeftIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  LightBulbIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline'
import clsx from 'clsx'

// ─── Types ───────────────────────────────────────────────────────────

interface HelpSection {
  id: string
  title: string
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>
  description: string
  content: ContentBlock[]
}

type ContentBlock =
  | { type: 'heading'; level: 2 | 3; text: string; id?: string }
  | { type: 'paragraph'; text: string }
  | { type: 'list'; ordered?: boolean; items: string[] }
  | { type: 'callout'; variant: 'info' | 'tip' | 'warning' | 'success'; title?: string; text: string }
  | { type: 'table'; headers: string[]; rows: string[][] }
  | { type: 'accordion'; title: string; content: ContentBlock[] }
  | { type: 'status-card'; items: { status: string; color: string; description: string; action: string }[] }
  | { type: 'feature-grid'; items: { title: string; description: string; icon?: string }[] }
  | { type: 'divider' }

// ─── Content Data ────────────────────────────────────────────────────

const helpSections: HelpSection[] = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    icon: RocketLaunchIcon,
    description: 'New to Guardian? Start here for a complete overview.',
    content: [
      { type: 'heading', level: 2, text: 'What is iuvo Guardian?', id: 'what-is-guardian' },
      { type: 'paragraph', text: 'iuvo Guardian is a comprehensive Microsoft 365 security compliance platform that continuously monitors your M365 environment for configuration drift, evaluates compliance against industry frameworks, and provides actionable security insights for your organization.' },
      { type: 'heading', level: 3, text: 'Core Capabilities' },
      { type: 'feature-grid', items: [
        { title: 'Configuration Drift Detection', description: 'Real-time monitoring of M365 settings against your approved baselines', icon: 'drift' },
        { title: 'Multi-Framework Compliance', description: 'A comprehensive policy library evaluating your tenant against CISA SCuBA, NIST, MCSB, EIDSCA, and Maester frameworks', icon: 'compliance' },
        { title: 'User Analytics', description: 'License utilization, MFA adoption, phish-resistant authentication, and inactive user detection', icon: 'users' },
        { title: 'OAuth App Monitoring', description: 'AI-enriched risk classification of third-party OAuth applications in your tenant', icon: 'apps' },
        { title: 'Executive Reporting', description: 'PDF/HTML reports with AI-generated executive summaries and compliance trends', icon: 'reports' },
        { title: 'Security Posture Hub', description: 'Unified dashboard combining Best Practice Analyzer, Secure Score, and SCuBA assessments', icon: 'security' },
      ]},
      { type: 'heading', level: 2, text: 'Your First Steps', id: 'first-steps' },
      { type: 'list', ordered: true, items: [
        '**Review the Command Center** — Get an overview of your tenant\'s security health, active alerts, and compliance scores',
        '**Explore the Security Center** — View your Guardian Score and Best Practice Analyzer results across every compliance framework you have starred',
        '**Check User Analytics** — Review MFA adoption, phish-resistant auth coverage, and license utilization',
        '**Run a Configuration Scan** — Navigate to Tenant Management, select your tenant, and click Scan Now',
        '**Review Detected Drift** — Check the Baseline Compliance page for any configuration changes',
        '**Generate a Report** — Use Executive Report to create a PDF/HTML summary for stakeholders',
      ]},
      { type: 'heading', level: 2, text: 'Key Concepts', id: 'key-concepts' },
      { type: 'table', headers: ['Concept', 'Description'], rows: [
        ['Configuration Drift', 'When your actual M365 settings differ from your approved baselines'],
        ['Guardian Score', 'A composite compliance metric aggregated from your starred compliance frameworks (A-F letter grade)'],
        ['Compliance Policies', 'Security rules that evaluate your tenant configuration against compliance frameworks'],
        ['Waiver', 'An exception for a compliance violation — can be permanent, temporary (auto-expiring), or not-applicable'],
        ['Baseline', 'Your approved "gold standard" M365 configuration that Guardian monitors against'],
        ['Phish-Resistant MFA', 'Strong authentication methods: FIDO2 security keys, Windows Hello for Business, certificate-based auth'],
      ]},
      { type: 'callout', variant: 'tip', title: 'Quick Tip', text: 'Use the sidebar navigation to jump between Guardian\'s key areas. The Command Center is your starting point for daily security monitoring.' },
    ]
  },
  {
    id: 'command-center',
    title: 'Command Center',
    icon: HomeIcon,
    description: 'Your operations dashboard for real-time tenant health.',
    content: [
      { type: 'heading', level: 2, text: 'Dashboard Overview', id: 'dashboard-overview' },
      { type: 'paragraph', text: 'The Command Center is your primary operations hub, providing a real-time overview of your Microsoft 365 security posture. It aggregates data from drift detection, compliance evaluation, and user analytics into a single view.' },
      { type: 'heading', level: 3, text: 'Key Metrics' },
      { type: 'list', items: [
        '**Guardian Score** — Your overall compliance score with letter grade (A-F) based on starred frameworks',
        '**Active Drift Count** — Number of unresolved configuration drift items by severity',
        '**Compliance Violations** — Current violations across all evaluated frameworks',
        '**User Risk Indicators** — MFA adoption rate, inactive users, and license utilization',
      ]},
      { type: 'heading', level: 3, text: 'Interactive Features' },
      { type: 'list', items: [
        '**Clickable metric cards** — Click any metric to navigate directly to the detailed view with appropriate filters applied',
        '**Recent Activity Feed** — Real-time feed of drift detections, approvals, and compliance changes',
        '**Drift Trends** — 30-day visualization of drift detection and resolution trends',
        '**Top Risks** — Severity-ranked list of your most critical security findings',
      ]},
      { type: 'callout', variant: 'info', text: 'Dashboard data refreshes automatically. You can also trigger manual scans from Tenant Management for immediate updates.' },
    ]
  },
  {
    id: 'baseline-compliance',
    title: 'Baseline Compliance',
    icon: ExclamationTriangleIcon,
    description: 'Configuration drift detection and baseline management.',
    content: [
      { type: 'heading', level: 2, text: 'How Drift Detection Works', id: 'how-drift-works' },
      { type: 'paragraph', text: 'Guardian continuously exports your current M365 configuration and compares it against your approved baseline. When differences are detected, they are flagged as configuration drift with AI-generated analysis and severity scoring.' },
      { type: 'heading', level: 3, text: 'Detection Pipeline' },
      { type: 'list', ordered: true, items: [
        'Scheduled or manual scan is triggered',
        'Guardian exports your current M365 configuration',
        'Current configuration is compared against your approved baseline',
        'Differences are identified and categorized by component type and severity',
        'AI analysis generates human-readable descriptions and remediation guidance',
        'Results appear on the Baseline Compliance page for review',
      ]},
      { type: 'heading', level: 2, text: 'Reviewing Drift', id: 'reviewing-drift' },
      { type: 'paragraph', text: 'Each drift item shows the affected component, property, before/after values, AI-generated description, severity level, and recommended action. Click any drift item to open the detail drawer for a comprehensive view.' },
      { type: 'heading', level: 3, text: 'Drift Detail Drawer' },
      { type: 'list', items: [
        '**Before/After Comparison** — See exactly what changed with highlighted differences',
        '**AI Analysis** — Human-readable explanation of the change and its security implications',
        '**Severity Assessment** — A composite severity informed by the property changed, the compliance impact of the change, and AI risk analysis',
        '**Remediation Guidance** — Recommended steps to address the drift',
        '**Audit History** — Complete timeline of status changes and decisions',
      ]},
      { type: 'heading', level: 2, text: 'How Severity Is Determined', id: 'drift-severity-scoring' },
      { type: 'paragraph', text: 'Drift severity is not a single signal — it is the strictest result from three independent assessments. Severity only ever escalates upward through the pipeline so that a high-risk change can never be hidden by a softer score from another layer.' },
      { type: 'list', ordered: true, items: [
        '**Property-level baseline** — Some properties are inherently more sensitive than others (for example, a Conditional Access policy state change is treated more seriously than a display-name edit). Each property carries a baseline severity that establishes the floor.',
        '**Compliance impact** — Guardian re-evaluates your tenant against its compliance frameworks after the change is detected. If the drift introduces a new violation, the drift\'s severity is elevated to match the criticality of the broken requirement (Critical for SHALL-level controls, High for SHOULD-level, Medium for MAY-level). Drifts that resolve a prior violation are noted but do not lower severity.',
        '**AI risk analysis** — Guardian\'s AI reviews the full change in context (the resource type, the values involved, related security signals) and may elevate severity further if it detects a risk pattern the first two layers missed.',
      ]},
      { type: 'callout', variant: 'tip', title: 'Why drift and compliance are connected', text: 'A configuration change that breaks a critical compliance control should never appear as a low-severity drift. By tying compliance evaluation directly into severity scoring, Guardian ensures that drifts which create real audit or framework gaps are surfaced at the seriousness they deserve — without requiring you to cross-reference the Best Practice Analyzer manually.' },
      { type: 'heading', level: 2, text: 'Making Decisions', id: 'making-decisions' },
      { type: 'table', headers: ['Action', 'When to Use', 'What Happens'], rows: [
        ['Approve', 'Legitimate business change that should become the new standard', 'Baseline is updated automatically; drift status becomes Remediated after next scan'],
        ['Reject', 'Unauthorized or incorrect change', 'Drift is flagged; your iuvo client team is notified. If change persists, it reappears as New'],
        ['Ignore', 'Acceptable temporary deviation', 'Acknowledged but baseline unchanged. May reappear if value changes further'],
      ]},
      { type: 'heading', level: 2, text: 'Bulk Operations', id: 'bulk-operations' },
      { type: 'paragraph', text: 'Select multiple drift items using checkboxes to perform bulk approvals. The system will show an impact warning before processing, including any additional drifts that may be automatically included for resource-level updates.' },
      { type: 'callout', variant: 'warning', title: 'Resource-Level Updates', text: 'Some bulk approvals may include additional related drifts for complex resources. Always review the impact summary before confirming.' },
      { type: 'heading', level: 2, text: 'Baseline Management', id: 'baseline-management' },
      { type: 'list', items: [
        '**Change Tracker** — Monitor the status of baseline updates created from approved drifts',
        '**Baseline Export** — Download your current baseline configuration',
        '**Automatic Updates** — Approved drifts automatically trigger baseline updates',
      ]},
    ]
  },
  {
    id: 'security-center',
    title: 'Security Center',
    icon: ShieldCheckIcon,
    description: 'Unified security assessment hub with multiple evaluation tools.',
    content: [
      { type: 'heading', level: 2, text: 'Security Center Overview', id: 'security-overview' },
      { type: 'paragraph', text: 'The Security Center consolidates all security assessments into a single hub with five tabs: Overview, Best Practice Analyzer, Secure Score, SCuBA Deep Scan, and App Permissions.' },
      { type: 'heading', level: 2, text: 'Overview Tab', id: 'overview-tab' },
      { type: 'list', items: [
        '**Guardian Score Dashboard** — Your composite compliance metric with A-F letter grade',
        '**Framework Status Cards** — Per-framework compliance scores and pass/fail counts',
        '**Recent Violations** — Latest compliance findings ranked by severity',
        '**Quick Navigation** — Links to detailed assessments in other tabs',
      ]},
      { type: 'heading', level: 2, text: 'Best Practice Analyzer', id: 'bpa' },
      { type: 'paragraph', text: 'The BPA evaluates your M365 tenant against a comprehensive policy library spanning Identity, Exchange, Defender, Teams, and SharePoint — mapped to the CISA SCuBA, NIST, MCSB, EIDSCA, and Maester frameworks.' },
      { type: 'heading', level: 3, text: 'Framework Cards' },
      { type: 'paragraph', text: 'Each framework card shows the number of passing, failing, and waived policies. Click a framework card to filter the violations table to that specific framework.' },
      { type: 'heading', level: 3, text: 'Violations Table' },
      { type: 'list', items: [
        '**Policy ID** — Unique identifier (e.g., GUARD.IAM.001)',
        '**Criticality** — SHALL (Critical), SHOULD (High), MAY (Medium)',
        '**Status** — Pass, Fail, or Waived',
        '**Frameworks** — Which frameworks this policy maps to',
        '**Remediation** — Guidance on how to address the violation',
      ]},
      { type: 'callout', variant: 'tip', title: 'All Controls View', text: 'Use the "All Controls" view to see every evaluated policy including passing ones, not just violations.' },
      { type: 'heading', level: 2, text: 'Microsoft Secure Score', id: 'secure-score' },
      { type: 'paragraph', text: 'Integrates with the Microsoft Secure Score API to display your tenant\'s score, improvement actions, and estimated point gains. Use the Sync button to refresh data from Microsoft.' },
      { type: 'heading', level: 2, text: 'SCuBA Deep Scan', id: 'scuba' },
      { type: 'paragraph', text: 'Performs a CISA SCuBA (Secure Cloud Business Applications) baseline evaluation against federal M365 security requirements per BOD 25-01.' },
      { type: 'heading', level: 2, text: 'App Permissions', id: 'app-permissions' },
      { type: 'paragraph', text: 'Monitors OAuth applications registered in your tenant for security risks. Features include:' },
      { type: 'list', items: [
        '**Risk Classification** — Applications rated as Critical, High, Medium, or Low risk based on permission tiers',
        '**AI Risk Analysis** — AI-generated risk summaries and remediation recommendations',
        '**Stale App Detection** — Identifies applications with no sign-in activity for 90+ days',
        '**First-Party Filtering** — Microsoft-owned apps are deprioritized in risk assessments',
        '**Permission Tier Breakdown** — Detailed view of each app\'s permission scope and risk level',
      ]},
    ]
  },
  {
    id: 'user-analytics',
    title: 'User Analytics',
    icon: UsersIcon,
    description: 'MFA adoption, license utilization, and user risk insights.',
    content: [
      { type: 'heading', level: 2, text: 'User Analytics Overview', id: 'analytics-overview' },
      { type: 'paragraph', text: 'The User Analytics page provides comprehensive insights into your Microsoft 365 user population, including authentication methods, license utilization, and user activity patterns.' },
      { type: 'heading', level: 2, text: 'MFA Adoption', id: 'mfa-adoption' },
      { type: 'list', items: [
        '**Overall MFA Rate** — Percentage of users with MFA enabled',
        '**MFA Method Breakdown** — Distribution of authentication methods across your user population',
        '**Users Without MFA** — List of users who have not enrolled in multi-factor authentication',
      ]},
      { type: 'heading', level: 2, text: 'Phish-Resistant Authentication', id: 'phish-resistant' },
      { type: 'paragraph', text: 'Guardian specifically tracks adoption of phish-resistant MFA methods, which are considered the strongest form of authentication:' },
      { type: 'table', headers: ['Method', 'Description'], rows: [
        ['FIDO2 Security Keys', 'Hardware security keys (e.g., YubiKey) that use public key cryptography'],
        ['Windows Hello for Business', 'Biometric or PIN-based authentication tied to the device TPM'],
        ['Certificate-Based Auth', 'X.509 certificate authentication using smart cards or virtual smart cards'],
      ]},
      { type: 'callout', variant: 'info', text: 'Phish-resistant MFA methods are immune to common phishing attacks because they bind authentication to the legitimate service — attackers cannot relay or replay the credential.' },
      { type: 'heading', level: 2, text: 'License Utilization', id: 'license-utilization' },
      { type: 'list', items: [
        '**Active vs. Inactive Users** — Identify licensed users who haven\'t signed in recently',
        '**Inactivity Threshold** — Configurable per-tenant (default: 90 days)',
        '**License Waste Analysis** — Shared mailbox detection to identify licenses that can be reclaimed',
      ]},
      { type: 'heading', level: 2, text: 'Trend Analysis', id: 'trend-analysis' },
      { type: 'paragraph', text: 'Historical charts show week-over-week trends for MFA adoption, phish-resistant auth coverage, and user activity. Use these trends to track the effectiveness of your security initiatives over time.' },
      { type: 'heading', level: 2, text: 'CSV Export', id: 'csv-export' },
      { type: 'paragraph', text: 'Export the "Users Requiring Attention" table to CSV for offline analysis, reporting, or integration with other tools.' },
    ]
  },
  {
    id: 'compliance-frameworks',
    title: 'Compliance Frameworks',
    icon: ClipboardDocumentCheckIcon,
    description: 'The security frameworks Guardian evaluates against.',
    content: [
      { type: 'heading', level: 2, text: 'Supported Frameworks', id: 'supported-frameworks' },
      { type: 'paragraph', text: 'Guardian evaluates your Microsoft 365 tenant against a slate of industry-recognized security frameworks, with policies spanning Identity, Exchange, Defender, Teams, and SharePoint.' },
      { type: 'table', headers: ['Framework', 'Focus'], rows: [
        ['CISA SCuBA', 'Federal M365 security baselines per BOD 25-01 — Secure Cloud Business Applications'],
        ['NIST SP 800-53 Rev 5', 'Comprehensive security and privacy controls for information systems'],
        ['NIST CSF 2.0', 'Cybersecurity Framework for managing and reducing cybersecurity risk'],
        ['Microsoft MCSB', 'Microsoft Cloud Security Benchmark — Microsoft\'s recommended cloud security controls'],
        ['EIDSCA', 'Entra ID Security Config Analyzer — focused on identity security configuration'],
        ['Maester', 'M365 Security Testing Framework — community-driven M365 security tests'],
      ]},
      { type: 'heading', level: 2, text: 'Policy Classification', id: 'policy-classification' },
      { type: 'heading', level: 3, text: 'Criticality Levels' },
      { type: 'table', headers: ['Level', 'Keyword', 'Meaning'], rows: [
        ['Critical', 'SHALL', 'Mandatory requirement — non-compliance represents a significant security risk'],
        ['High', 'SHOULD', 'Strongly recommended — should be implemented unless there is a documented exception'],
        ['Medium', 'MAY', 'Optional but beneficial — consider implementing based on your risk tolerance'],
      ]},
      { type: 'heading', level: 3, text: 'Workloads' },
      { type: 'list', items: [
        '**Identity** (Entra ID) — Conditional Access, MFA, PIM, authentication methods, identity protection',
        '**Exchange Online** — Mail flow rules, anti-spam, anti-malware, DLP, audit logging',
        '**Microsoft Defender** — Safe Links, Safe Attachments, anti-phishing, threat protection',
        '**Teams** — External access, guest policies, meeting settings, messaging policies',
        '**SharePoint Online** — Sharing settings, access control, site creation, OneDrive policies',
      ]},
      { type: 'heading', level: 2, text: 'Guardian Score', id: 'guardian-score' },
      { type: 'paragraph', text: 'Your Guardian Score is a composite compliance metric calculated from your starred frameworks. It provides an A-F letter grade alongside a numeric percentage. You can customize which frameworks contribute to your score by starring/unstarring them in the Security Center.' },
      { type: 'callout', variant: 'tip', title: 'Framework Flexibility', text: 'You can assign or unassign frameworks based on your organization\'s regulatory requirements. Only starred frameworks contribute to your Guardian Score.' },
    ]
  },
  {
    id: 'executive-reports',
    title: 'Executive Reports',
    icon: DocumentChartBarIcon,
    description: 'Generate PDF/HTML compliance reports for stakeholders.',
    content: [
      { type: 'heading', level: 2, text: 'Report Generation', id: 'report-generation' },
      { type: 'paragraph', text: 'The Executive Report feature generates professional compliance reports suitable for executive stakeholders, board presentations, and MSP client distribution.' },
      { type: 'heading', level: 3, text: 'Report Contents' },
      { type: 'list', items: [
        '**Guardian Score Dashboard** — Current composite score with A-F letter grade and trend arrows',
        '**AI Executive Summary** — AI-generated 3-4 paragraph narrative summary of your compliance posture',
        '**Framework Deep Dives** — Per-framework compliance scores with category breakdowns',
        '**Compliance Trends** — Score history and improvement trajectory over configurable periods (30/60/90 days)',
        '**Violation Summary** — Key findings ranked by severity with remediation status',
        '**Metric Snapshots** — Violations resolved, drift remediated, MFA adoption rates',
      ]},
      { type: 'heading', level: 3, text: 'Export Options' },
      { type: 'list', items: [
        '**PDF** — Professional branded document for printing and sharing',
        '**HTML** — Interactive web-based report for browser viewing',
      ]},
      { type: 'callout', variant: 'info', text: 'Reports are generated on-demand from your current compliance data. Allow a few moments for the AI executive summary to be generated.' },
    ]
  },
  {
    id: 'drift-management',
    title: 'Drift Management',
    icon: ArrowPathIcon,
    description: 'Approval workflows, bulk operations, and baseline updates.',
    content: [
      { type: 'heading', level: 2, text: 'Drift Lifecycle', id: 'drift-lifecycle' },
      { type: 'paragraph', text: 'Every detected drift follows a defined lifecycle from detection through resolution. Understanding this lifecycle helps you manage drift efficiently.' },
      { type: 'status-card', items: [
        { status: 'New', color: 'amber', description: 'Recently detected configuration change', action: 'Review and decide: Approve, Reject, or Ignore' },
        { status: 'Pending', color: 'amber', description: 'Approved and queued for batch processing', action: 'None — will be included in next batch update' },
        { status: 'PendingApproval', color: 'blue', description: 'Requires additional approval before processing', action: 'Manager or designated approver must review' },
        { status: 'Approved', color: 'emerald', description: 'Accepted change — baseline update in progress', action: 'None — baseline update being processed' },
        { status: 'Rejected', color: 'red', description: 'Unauthorized or incorrect change', action: 'Investigate and manually fix in M365. Your iuvo team is notified.' },
        { status: 'AwaitingRemediation', color: 'slate', description: 'Processed, waiting for next scan confirmation', action: 'None — allow 24-48 hours for next scan cycle' },
        { status: 'Remediated', color: 'emerald', description: 'Successfully resolved — no longer detected', action: 'None — change is now part of your standard config' },
        { status: 'Ignored', color: 'blue', description: 'Acknowledged but baseline not updated', action: 'Monitor occasionally. Returns as New if value changes.' },
      ]},
      { type: 'heading', level: 2, text: 'Automatic Reconciliation', id: 'reconciliation' },
      { type: 'paragraph', text: 'Every scan automatically reconciles drift statuses:' },
      { type: 'list', items: [
        '**Approved → Remediated** — When the approved drift is no longer detected',
        '**Rejected → New** — If the rejected change reappears in a scan',
        '**Remediated → New** — If a previously fixed drift returns (regression detection)',
      ]},
      { type: 'heading', level: 2, text: 'Batch Processing', id: 'batch-processing' },
      { type: 'paragraph', text: 'Individual approvals are queued for later batch processing, combining multiple changes into a single baseline update per tenant. Bulk approvals trigger immediate batch updates.' },
      { type: 'heading', level: 3, text: 'Property-Level vs. Resource-Level Updates' },
      { type: 'table', headers: ['Type', 'When Used', 'Impact'], rows: [
        ['Property-Level', 'Individual settings changed (permissions, timeouts)', 'Only the specific changed property is updated — cleaner, more granular'],
        ['Resource-Level', 'Complex nested objects, new resources, interdependent settings', 'Entire configuration block is updated — may include related drifts'],
      ]},
    ]
  },
  {
    id: 'waivers',
    title: 'Waivers & Exceptions',
    icon: ShieldExclamationIcon,
    description: 'Managing compliance exceptions and policy waivers.',
    content: [
      { type: 'heading', level: 2, text: 'Waiver System', id: 'waiver-system' },
      { type: 'paragraph', text: 'The waiver system allows you to create documented exceptions for compliance violations when a policy does not apply to your environment or when you have an accepted risk.' },
      { type: 'heading', level: 3, text: 'Waiver Types' },
      { type: 'table', headers: ['Type', 'Duration', 'Use Case'], rows: [
        ['Permanent', 'No expiry', 'Policy does not apply to your organization\'s operating model'],
        ['Temporary', 'Auto-expires on set date', 'Accepted risk with a planned remediation date'],
        ['Not Applicable', 'No expiry', 'Policy evaluates a feature/service your organization does not use'],
      ]},
      { type: 'heading', level: 2, text: 'Creating Waivers', id: 'creating-waivers' },
      { type: 'list', ordered: true, items: [
        'Navigate to the **Security Center → Best Practice Analyzer**',
        'Find the violation you want to waive in the violations table',
        'Click the **Waive** button on the violation row',
        'Select the waiver type (Permanent, Temporary, or Not Applicable)',
        'For Temporary waivers, set the expiration date',
        'Add a justification explaining why this waiver is appropriate',
        'Click **Create Waiver** to save',
      ]},
      { type: 'heading', level: 2, text: 'Waiver Impact on Scoring', id: 'waiver-scoring' },
      { type: 'callout', variant: 'info', text: 'Waived violations are excluded from compliance score calculations. This means your Guardian Score and per-framework scores will reflect only the policies that actively apply to your environment.' },
      { type: 'heading', level: 2, text: 'Auto-Expiration', id: 'auto-expiration' },
      { type: 'paragraph', text: 'Temporary waivers automatically deactivate after their expiration date. When a waiver expires, the underlying violation becomes active again and will appear in your compliance results. All waiver lifecycle events are logged in the audit trail.' },
    ]
  },
  {
    id: 'status-reference',
    title: 'Status Reference',
    icon: InformationCircleIcon,
    description: 'Quick reference for all drift statuses, severities, and colors.',
    content: [
      { type: 'heading', level: 2, text: 'Drift Status Lifecycle', id: 'status-lifecycle' },
      { type: 'table', headers: ['Status', 'Color', 'Description', 'Auto Behavior'], rows: [
        ['New', 'Yellow', 'Just detected, awaiting review', 'None'],
        ['Pending', 'Yellow', 'Queued for batch processing', 'Batched with other approvals'],
        ['PendingApproval', 'Blue', 'Needs additional approval', 'None until approved'],
        ['Approved', 'Green', 'Accepted, baseline updating', 'Auto-updates baseline'],
        ['Rejected', 'Red', 'Unacceptable change', 'Reappears as New if detected again'],
        ['Remediated', 'Green', 'No longer detected', 'Returns as New if drift regresses'],
        ['Ignored', 'Blue', 'Acknowledged, no update', 'Reappears as New if value changes'],
        ['AwaitingRemediation', 'Gray', 'Processed, awaiting scan', 'Auto-updates to Remediated'],
      ]},
      { type: 'heading', level: 2, text: 'Severity Levels', id: 'severity-levels' },
      { type: 'table', headers: ['Severity', 'Color', 'Meaning'], rows: [
        ['Critical', 'Red', 'Immediate security risk — MFA weakening, admin role changes, external sharing escalation'],
        ['High', 'Orange', 'Significant impact likely — security policy changes, access control modifications'],
        ['Medium', 'Yellow', 'Moderate impact possible — configuration changes with indirect security implications'],
        ['Low', 'Green', 'Minimal impact expected — cosmetic or low-risk configuration changes'],
      ]},
      { type: 'heading', level: 2, text: 'Timeline Expectations', id: 'timeline' },
      { type: 'table', headers: ['Stage', 'Expected Duration'], rows: [
        ['Approval → Processing', 'Usually within 1 hour during business hours'],
        ['Processing → Baseline Update', '15-30 minutes for baseline integration'],
        ['Next Scan → Status Update', '24-48 hours to confirm change took effect'],
      ]},
    ]
  },
  {
    id: 'troubleshooting',
    title: 'Troubleshooting',
    icon: WrenchScrewdriverIcon,
    description: 'Common issues and their solutions.',
    content: [
      { type: 'heading', level: 2, text: 'Authentication & Access', id: 'auth-issues' },
      { type: 'accordion', title: 'Can\'t login or see "Access Denied"', content: [
        { type: 'list', ordered: true, items: [
          'Clear browser cache and cookies',
          'Try an incognito/private browsing window',
          'Sign out of all Microsoft accounts, then sign back in',
          'Ensure you\'re using the correct Microsoft Entra ID account',
          'Contact your administrator to verify your user role assignment',
        ]},
      ]},
      { type: 'accordion', title: 'Can\'t see certain tenants', content: [
        { type: 'paragraph', text: 'Tenant visibility is controlled by user-tenant associations. If you were recently granted access, sign out and back in — permission changes can take 15-30 minutes to propagate.' },
      ]},
      { type: 'heading', level: 2, text: 'Data & Dashboard', id: 'data-issues' },
      { type: 'accordion', title: 'Dashboard shows old or missing data', content: [
        { type: 'list', ordered: true, items: [
          'Hard refresh the page (Ctrl+F5 on Windows, Cmd+Shift+R on Mac)',
          'Verify recent scans completed successfully in Tenant Management',
          'Run a manual scan to force fresh data collection',
          'Dashboard data auto-refreshes — allow a few minutes',
        ]},
      ]},
      { type: 'accordion', title: '"No Data Available" messages', content: [
        { type: 'paragraph', text: 'This typically means no scans have run yet for the selected tenant, or the most recent scan failed. Go to Tenant Management, select the tenant, and click "Scan Now" to initiate data collection. Initial scans take 15-30 minutes.' },
      ]},
      { type: 'heading', level: 2, text: 'Drift & Compliance', id: 'drift-issues' },
      { type: 'accordion', title: 'Approved drift still shows "AwaitingRemediation"', content: [
        { type: 'callout', variant: 'success', title: 'This is normal!', text: 'After approval, allow 24-48 hours for processing and the next scan cycle. The status automatically updates to "Remediated" once the next scan confirms the drift is resolved. Contact support only if stuck for more than 48 hours.' },
      ]},
      { type: 'accordion', title: 'Can\'t approve or reject drifts', content: [
        { type: 'list', items: [
          'Verify you have the **Approver** or **Admin** role for this tenant',
          'Refresh the page and try again',
          'Try a different browser to rule out browser-specific issues',
          'If bulk operations fail, try processing items individually',
        ]},
      ]},
      { type: 'accordion', title: 'No drift detected despite known changes', content: [
        { type: 'list', items: [
          'Changes may not be reflected until the next scan cycle',
          'Verify the changed items are covered by your baseline configuration',
          'Run a manual scan to force immediate detection',
          'Some M365 changes take time to propagate through Microsoft\'s APIs',
        ]},
      ]},
      { type: 'heading', level: 2, text: 'Scanning Issues', id: 'scan-issues' },
      { type: 'accordion', title: 'Scan never completes or appears hung', content: [
        { type: 'paragraph', text: 'Complex environments can take 45+ minutes to scan. Check the scan job status for progress updates. If a scan appears stuck for more than 2 hours, contact your administrator. Try scheduling scans during off-peak hours for better performance.' },
      ]},
      { type: 'heading', level: 2, text: 'When to Contact Support', id: 'contact-support' },
      { type: 'callout', variant: 'warning', title: 'Contact support immediately for:', text: 'Security concerns (suspicious unauthorized changes), complete system unavailability, data corruption, or persistent authentication failures. Include the exact error message, steps to reproduce, browser/OS info, tenant details, and screenshots.' },
    ]
  },
  {
    id: 'faq',
    title: 'FAQ',
    icon: QuestionMarkCircleIcon,
    description: 'Frequently asked questions and quick answers.',
    content: [
      { type: 'heading', level: 2, text: 'General', id: 'faq-general' },
      { type: 'accordion', title: 'What is configuration drift?', content: [
        { type: 'paragraph', text: 'Configuration drift occurs when your Microsoft 365 environment\'s actual settings differ from your approved baseline. This can happen due to manual changes by administrators, third-party tools, Microsoft updates, or unauthorized modifications.' },
      ]},
      { type: 'accordion', title: 'How often does Guardian scan my environment?', content: [
        { type: 'paragraph', text: 'Scan frequency is configurable per-tenant — options include Manual, Daily, or Weekly schedules. Your administrator can also trigger on-demand scans at any time. User analytics data refreshes every 6 hours automatically.' },
      ]},
      { type: 'accordion', title: 'Will Guardian automatically change my M365 settings?', content: [
        { type: 'callout', variant: 'success', text: 'No. Guardian is strictly a monitoring platform. It detects drift and evaluates compliance but never modifies your M365 configuration. All baseline changes require your explicit approval.' },
      ]},
      { type: 'heading', level: 2, text: 'Compliance', id: 'faq-compliance' },
      { type: 'accordion', title: 'What is the Guardian Score?', content: [
        { type: 'paragraph', text: 'The Guardian Score is a composite compliance metric (0-100% with A-F letter grade) calculated from your starred compliance frameworks. It aggregates pass/fail results from policy evaluations. You can customize which frameworks contribute by starring/unstarring them in the Security Center.' },
      ]},
      { type: 'accordion', title: 'Can I choose which frameworks to evaluate against?', content: [
        { type: 'paragraph', text: 'Yes. Guardian supports CISA SCuBA, NIST SP 800-53, NIST CSF 2.0, Microsoft MCSB, EIDSCA, and Maester. You can assign or unassign frameworks based on your regulatory requirements. Only starred frameworks contribute to your Guardian Score.' },
      ]},
      { type: 'accordion', title: 'What happens when I waive a violation?', content: [
        { type: 'paragraph', text: 'Waived violations are excluded from compliance score calculations. Permanent waivers remain indefinitely. Temporary waivers auto-expire on the set date, causing the violation to become active again. All waiver events are logged in the audit trail.' },
      ]},
      { type: 'heading', level: 2, text: 'Security & Privacy', id: 'faq-security' },
      { type: 'accordion', title: 'Can Guardian access my emails, files, or passwords?', content: [
        { type: 'callout', variant: 'info', text: 'No. Guardian only reads configuration settings (policies, rules, administrative settings). It does not access email content, file contents, user personal data, or passwords. The Global Reader role enforces read-only access at the directory level.' },
      ]},
      { type: 'accordion', title: 'Why do some permissions say "ReadWrite"?', content: [
        { type: 'paragraph', text: 'A small number of permissions (ChannelSettings.ReadWrite.All, DeviceManagementConfiguration.ReadWrite.All) require the write scope to decrypt certain encrypted settings during configuration exports. Guardian does not perform write operations, and the Global Reader role prevents any actual modifications. You can verify this in your Entra ID audit logs.' },
      ]},
      { type: 'accordion', title: 'Is my data stored outside my tenant?', content: [
        { type: 'paragraph', text: 'Yes — configuration data (policy settings, security rules, tenant metadata) is stored in iuvo\'s secure Azure infrastructure to enable drift detection, baseline management, and reporting. This data is encrypted at rest and in transit. Guardian does NOT collect email content, file contents, user passwords, or personal data.' },
      ]},
      { type: 'heading', level: 2, text: 'Technical', id: 'faq-technical' },
      { type: 'accordion', title: 'What does a "GUARD.IAM.001" policy ID mean?', content: [
        { type: 'paragraph', text: 'Policy IDs follow the pattern GUARD.<SERVICE>.<NUMBER>. "GUARD" is the Guardian prefix, the service code indicates the workload (IAM = Identity, EXO = Exchange, DEF = Defender, TMS = Teams, SPO = SharePoint), and the number is sequential. Example: GUARD.IAM.001 is the first Identity policy.' },
      ]},
      { type: 'accordion', title: 'What are phish-resistant MFA methods?', content: [
        { type: 'paragraph', text: 'Phish-resistant MFA methods are authentication mechanisms immune to common phishing attacks: FIDO2 security keys (e.g., YubiKey), Windows Hello for Business (biometric/PIN tied to TPM), and certificate-based authentication. These bind authentication to the legitimate service, preventing relay or replay attacks.' },
      ]},
      { type: 'accordion', title: 'How do I get help or report issues?', content: [
        { type: 'list', items: [
          'Contact your iuvo Guardian administrator for account and access questions',
          'Email **guardian-support@iuvotech.com** for technical support',
          'Include error messages, screenshots, and steps to reproduce when reporting issues',
        ]},
      ]},
    ]
  },
  {
    id: 'changelog',
    title: 'Change Log',
    icon: DocumentTextIcon,
    description: 'History of updates, improvements, and new features.',
    content: [
      { type: 'heading', level: 2, text: 'Version 3.2.0', id: 'v3-2' },
      { type: 'heading', level: 3, text: 'New Features' },
      { type: 'list', items: [
        '**Purview DLP Data Collection** — Guardian now collects Microsoft Purview Data Loss Prevention policy configurations',
        '**Exchange Audit Log Collection** — New data source for Exchange Online audit logging configurations',
        '**Teams Policy Collection** — Expanded Teams policy data collection for compliance evaluation',
        '**Expanded Policy Coverage** — New compliance policies covering additional M365 workload areas',
      ]},
      { type: 'divider' },
      { type: 'heading', level: 2, text: 'Version 3.1.0 — March 2026', id: 'v3-1' },
      { type: 'heading', level: 3, text: 'New Features' },
      { type: 'list', items: [
        '**Phish-Resistant MFA Reporting** — FIDO2, Windows Hello for Business, and certificate-based auth tracking',
        '**User Analytics Trends** — Historical charts showing MFA adoption and user activity trends over time',
        '**BPA All Controls View** — See every evaluated policy including passing ones, not just violations',
        '**First-Party OAuth Exclusion** — Microsoft-owned apps deprioritized in OAuth app risk summaries',
        '**UI Redesign** — Dark theme refresh across User Analytics and Security Center pages',
      ]},
      { type: 'divider' },
      { type: 'heading', level: 2, text: 'Version 3.0.0 — February 2026', id: 'v3-0' },
      { type: 'heading', level: 3, text: 'Major Features' },
      { type: 'list', items: [
        '**Standards Compliance Engine** — 112 policies evaluating against 6 compliance frameworks',
        '**Security Center Hub** — Unified security assessment dashboard with BPA, Secure Score, SCuBA, and App Permissions tabs',
        '**OAuth App Permission Monitoring** — AI-enriched risk classification of third-party OAuth applications',
        '**Executive Report Generation** — PDF/HTML reports with AI-powered executive summaries',
        '**User Insights Expansion** — License utilization, MFA adoption, and inactive user detection',
        '**Self-Service Signup** — Tenant self-onboarding with admin consent flow',
      ]},
      { type: 'divider' },
      { type: 'heading', level: 2, text: 'Version 2.6.0 — November 2025', id: 'v2-6' },
      { type: 'heading', level: 3, text: 'Features & Improvements' },
      { type: 'list', items: [
        '**Comprehensive Drift Audit Logging** — Complete audit trail for all drift decisions with timestamps and user tracking',
        '**AI-Enhanced Severity Scoring** — AI detects 7 critical security patterns; MFA weakening scored ≥90% (Critical)',
        '**Enhanced Dashboard** — Component/property names in drift titles, severity badges, daily summaries',
        '**Performance** — Dashboard loads 99.98% faster (43MB → 10KB payload, 70s → <1s response time)',
      ]},
      { type: 'divider' },
      { type: 'heading', level: 2, text: 'Version 2.5.0 — September 2025', id: 'v2-5' },
      { type: 'list', items: [
        '**Improved Drift Detection** — Eliminates duplicate drift detection for complex nested M365 objects',
        '**Property-Level Baseline Updates** — Granular control with selective property updates in baselines',
        '**Live Dashboard Integration** — Real operational data in Recent Activity and Drift Trends',
      ]},
      { type: 'divider' },
      { type: 'heading', level: 2, text: 'Version 2.4.0 — August 2025', id: 'v2-4' },
      { type: 'list', items: [
        '**Async Scan Job Tracking** — Immediate job ID response with real-time progress polling',
        '**Batch Processing** — Intelligent batching combines multiple approvals into single baseline updates',
      ]},
      { type: 'divider' },
      { type: 'heading', level: 2, text: 'Earlier Versions', id: 'earlier' },
      { type: 'accordion', title: 'Version 2.0–2.3 (August 2025)', content: [
        { type: 'list', items: [
          '**v2.3** — Professional dashboard with clickable metric cards, URL parameter support, breadcrumb navigation',
          '**v2.2** — Production drift detection, comprehensive status lifecycle, drift reconciliation',
          '**v2.1** — Multi-tenant architecture, RBAC, Microsoft Entra ID authentication',
          '**v2.0** — New platform architecture, core drift detection',
        ]},
      ]},
      { type: 'accordion', title: 'Version 1.0 (July 2025)', content: [
        { type: 'list', items: [
          'Initial release with M365 configuration monitoring',
          'Basic drift detection and baseline management',
          'Manual processing workflows',
        ]},
      ]},
      { type: 'paragraph', text: 'Last Updated: March 2026' },
    ]
  },
]

// ─── Utility: Extract headings from content for "On this page" ──────

function extractHeadings(content: ContentBlock[]): { id: string; text: string; level: number }[] {
  return content
    .filter((block): block is Extract<ContentBlock, { type: 'heading' }> => block.type === 'heading' && block.level === 2 && !!block.id)
    .map(h => ({ id: h.id!, text: h.text, level: h.level }))
}

// ─── Utility: Search highlighting ────────────────────────────────────

function highlightText(text: string, query: string): React.ReactNode {
  if (!query || query.length < 3) return text
  const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi')
  const parts = text.split(regex)
  if (parts.length === 1) return text
  return parts.map((part, i) =>
    regex.test(part)
      ? <mark key={i} className="bg-amber-400/30 text-amber-200 rounded px-0.5">{part}</mark>
      : part
  )
}

// ─── Content Renderers ───────────────────────────────────────────────

function renderInlineMarkdown(text: string, searchQuery: string): React.ReactNode {
  // Process bold markers then highlight search matches
  const parts = text.split(/(\*\*.*?\*\*)/g)
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      const inner = part.slice(2, -2)
      return <strong key={i} className="text-slate-200 font-semibold">{highlightText(inner, searchQuery)}</strong>
    }
    // Process inline code
    const codeParts = part.split(/(`[^`]+`)/g)
    return codeParts.map((cp, j) => {
      if (cp.startsWith('`') && cp.endsWith('`')) {
        return <code key={`${i}-${j}`} className="text-primary-400 bg-dark-900/60 px-1.5 py-0.5 rounded text-[13px] font-mono">{cp.slice(1, -1)}</code>
      }
      return <Fragment key={`${i}-${j}`}>{highlightText(cp, searchQuery)}</Fragment>
    })
  })
}

function CalloutBox({ variant, title, text, searchQuery }: { variant: 'info' | 'tip' | 'warning' | 'success'; title?: string; text: string; searchQuery: string }) {
  const styles = {
    info: { border: 'border-primary-500/30', bg: 'bg-primary-500/5', icon: InformationCircleIcon, iconColor: 'text-primary-400', titleColor: 'text-primary-300' },
    tip: { border: 'border-emerald-500/30', bg: 'bg-emerald-500/5', icon: LightBulbIcon, iconColor: 'text-emerald-400', titleColor: 'text-emerald-300' },
    warning: { border: 'border-amber-500/30', bg: 'bg-amber-500/5', icon: ExclamationCircleIcon, iconColor: 'text-amber-400', titleColor: 'text-amber-300' },
    success: { border: 'border-emerald-500/30', bg: 'bg-emerald-500/5', icon: CheckCircleIcon, iconColor: 'text-emerald-400', titleColor: 'text-emerald-300' },
  }
  const s = styles[variant]
  const Icon = s.icon

  return (
    <div className={clsx('rounded-xl border p-4 flex gap-3', s.border, s.bg)}>
      <Icon className={clsx('h-5 w-5 shrink-0 mt-0.5', s.iconColor)} />
      <div>
        {title && <p className={clsx('font-semibold text-sm mb-1', s.titleColor)}>{title}</p>}
        <p className="text-slate-400 text-sm leading-relaxed">{renderInlineMarkdown(text, searchQuery)}</p>
      </div>
    </div>
  )
}

function AccordionBlock({ title, content, searchQuery }: { title: string; content: ContentBlock[]; searchQuery: string }) {
  const [open, setOpen] = useState(false)

  // Auto-open if search matches content inside
  useEffect(() => {
    if (searchQuery.length >= 3) {
      const hasMatch = JSON.stringify(content).toLowerCase().includes(searchQuery.toLowerCase())
        || title.toLowerCase().includes(searchQuery.toLowerCase())
      if (hasMatch) setOpen(true)
    }
  }, [searchQuery, content, title])

  return (
    <div className="border border-dark-700/60 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-4 bg-dark-800/40 hover:bg-dark-800/70 transition-colors text-left group"
      >
        <span className="font-medium text-slate-200 text-sm group-hover:text-text-primary">
          {highlightText(title, searchQuery)}
        </span>
        <ChevronDownIcon className={clsx('h-4 w-4 text-slate-500 transition-transform duration-200', open && 'rotate-180')} />
      </button>
      {open && (
        <div className="p-4 bg-dark-800/20 border-t border-dark-700/40 space-y-3">
          {content.map((block, i) => (
            <ContentRenderer key={i} block={block} searchQuery={searchQuery} />
          ))}
        </div>
      )}
    </div>
  )
}

function StatusCards({ items }: { items: { status: string; color: string; description: string; action: string }[] }) {
  const colorMap: Record<string, { border: string; dot: string; bg: string }> = {
    amber: { border: 'border-amber-500/30', dot: 'bg-amber-400', bg: 'bg-amber-500/5' },
    blue: { border: 'border-blue-500/30', dot: 'bg-blue-400', bg: 'bg-blue-500/5' },
    emerald: { border: 'border-emerald-500/30', dot: 'bg-emerald-400', bg: 'bg-emerald-500/5' },
    red: { border: 'border-red-500/30', dot: 'bg-red-400', bg: 'bg-red-500/5' },
    slate: { border: 'border-slate-600/30', dot: 'bg-slate-400', bg: 'bg-slate-500/5' },
  }

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {items.map((item) => {
        const c = colorMap[item.color] || colorMap.slate
        return (
          <div key={item.status} className={clsx('rounded-xl border p-4', c.border, c.bg)}>
            <div className="flex items-center gap-2 mb-2">
              <div className={clsx('w-2.5 h-2.5 rounded-full', c.dot)} />
              <span className="font-semibold text-slate-200 text-sm">{item.status}</span>
            </div>
            <p className="text-slate-400 text-xs mb-2">{item.description}</p>
            <p className="text-slate-500 text-xs"><span className="text-slate-400 font-medium">Action:</span> {item.action}</p>
          </div>
        )
      })}
    </div>
  )
}

function FeatureGrid({ items }: { items: { title: string; description: string }[] }) {
  // Brand-aligned gradients only: Soft Green (primary), Horizon Blue (secondaryBlue), Warm Sand (secondarySand).
  // Six unique combinations — three single-hue fades plus three two-hue brand blends.
  const iconColors = [
    // Soft Green primary
    'from-primary-500/20 to-primary-500/5 border-primary-500/30',
    // Horizon Blue
    'from-secondaryBlue-300/20 to-secondaryBlue-300/5 border-secondaryBlue-300/30',
    // Warm Sand
    'from-secondarySand-300/20 to-secondarySand-300/5 border-secondarySand-300/30',
    // Soft Green to Horizon Blue (cool blend)
    'from-primary-500/20 to-secondaryBlue-300/15 border-primary-500/25',
    // Warm Sand to Soft Green (warm-to-cool)
    'from-secondarySand-300/20 to-primary-500/10 border-secondarySand-300/30',
    // Horizon Blue to Warm Sand (sky to sand)
    'from-secondaryBlue-300/20 to-secondarySand-300/15 border-secondaryBlue-300/25',
  ]

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {items.map((item, i) => (
        <div key={i} className={clsx('rounded-xl border bg-gradient-to-br p-5 hover:scale-[1.02] transition-transform duration-200', iconColors[i % iconColors.length])}>
          <h4 className="font-semibold text-text-primary text-sm mb-2">{item.title}</h4>
          <p className="text-slate-400 text-xs leading-relaxed">{item.description}</p>
        </div>
      ))}
    </div>
  )
}

function ContentRenderer({ block, searchQuery }: { block: ContentBlock; searchQuery: string }) {
  switch (block.type) {
    case 'heading':
      if (block.level === 2) {
        return <h2 id={block.id} className="text-xl font-bold text-text-primary mt-8 mb-3 scroll-mt-24">{highlightText(block.text, searchQuery)}</h2>
      }
      return <h3 className="text-base font-semibold text-slate-200 mt-5 mb-2">{highlightText(block.text, searchQuery)}</h3>

    case 'paragraph':
      return <p className="text-slate-400 text-sm leading-relaxed">{renderInlineMarkdown(block.text, searchQuery)}</p>

    case 'list':
      const ListTag = block.ordered ? 'ol' : 'ul'
      return (
        <ListTag className={clsx('space-y-1.5 text-sm', block.ordered ? 'list-decimal' : 'list-disc', 'ml-5 marker:text-slate-600')}>
          {block.items.map((item, i) => (
            <li key={i} className="text-slate-400 leading-relaxed pl-1">
              {renderInlineMarkdown(item, searchQuery)}
            </li>
          ))}
        </ListTag>
      )

    case 'callout':
      return <CalloutBox variant={block.variant} title={block.title} text={block.text} searchQuery={searchQuery} />

    case 'table':
      return (
        <div className="overflow-x-auto rounded-xl border border-dark-700/60">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-dark-800/60">
                {block.headers.map((h, i) => (
                  <th key={i} className="text-left py-2.5 px-4 text-slate-400 font-medium text-xs uppercase tracking-wider border-b border-dark-700/40">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-dark-700/30">
              {block.rows.map((row, i) => (
                <tr key={i} className="hover:bg-dark-800/30 transition-colors">
                  {row.map((cell, j) => (
                    <td key={j} className={clsx('py-2.5 px-4', j === 0 ? 'text-slate-200 font-medium' : 'text-slate-400')}>
                      {renderInlineMarkdown(cell, searchQuery)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )

    case 'accordion':
      return <AccordionBlock title={block.title} content={block.content} searchQuery={searchQuery} />

    case 'status-card':
      return <StatusCards items={block.items} />

    case 'feature-grid':
      return <FeatureGrid items={block.items} />

    case 'divider':
      return <hr className="border-dark-700/40 my-6" />

    default:
      return null
  }
}

// ─── Main Help Page ──────────────────────────────────────────────────

export default function Help() {
  const location = useLocation()
  const navigate = useNavigate()
  const [activeSection, setActiveSection] = useState('getting-started')
  const [searchTerm, setSearchTerm] = useState('')
  const [searchResults, setSearchResults] = useState<string[]>([])
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  // Handle URL hash for deep linking
  useEffect(() => {
    const hash = location.hash.replace('#', '')
    if (hash) {
      // Check if hash is a section id
      const section = helpSections.find(s => s.id === hash)
      if (section) {
        setActiveSection(hash)
      } else {
        // Hash might be a heading within a section — find the right section
        for (const s of helpSections) {
          const headingMatch = s.content.find(
            (b): b is Extract<ContentBlock, { type: 'heading' }> => b.type === 'heading' && b.id === hash
          )
          if (headingMatch) {
            setActiveSection(s.id)
            // Scroll to heading after render
            requestAnimationFrame(() => {
              document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
            })
            break
          }
        }
      }
    }
  }, [location.hash])

  // Search functionality
  useEffect(() => {
    if (searchTerm.length >= 3) {
      const q = searchTerm.toLowerCase()
      const results = helpSections
        .filter(section => {
          const searchable = JSON.stringify(section).toLowerCase()
          return searchable.includes(q)
        })
        .map(section => section.id)
      setSearchResults(results)
      if (results.length > 0 && !results.includes(activeSection)) {
        setActiveSection(results[0])
      }
    } else {
      setSearchResults([])
    }
  }, [searchTerm])

  // Keyboard shortcut: Ctrl/Cmd+K to focus search
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        searchInputRef.current?.focus()
      }
      if (e.key === 'Escape') {
        searchInputRef.current?.blur()
        setSearchTerm('')
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [])

  const handleSectionChange = useCallback((id: string) => {
    setActiveSection(id)
    setMobileSidebarOpen(false)
    navigate(`#${id}`, { replace: true })
    // Scroll content to top
    contentRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
  }, [navigate])

  const activeContent = helpSections.find(s => s.id === activeSection)
  const headings = activeContent ? extractHeadings(activeContent.content) : []

  return (
    <div className="flex flex-col h-[calc(100vh-7rem)] -mt-4 -mx-4 sm:-mx-6 lg:-mx-8">
      {/* Header bar */}
      <div className="shrink-0 border-b border-dark-700/60 bg-dark-800/40 backdrop-blur-sm px-6 py-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            {/* Mobile menu toggle */}
            <button
              onClick={() => setMobileSidebarOpen(!mobileSidebarOpen)}
              className="lg:hidden p-2 -ml-2 text-slate-400 hover:text-slate-200 hover:bg-dark-700 rounded-lg transition-colors"
            >
              <Bars3BottomLeftIcon className="h-5 w-5" />
            </button>
            <div className="min-w-0">
              <h1 className="text-xl font-bold text-text-primary tracking-tight truncate">Help & Documentation</h1>
              <p className="text-xs text-slate-500 mt-0.5 hidden sm:block">Guides, references, and troubleshooting for iuvo Guardian</p>
            </div>
          </div>

          {/* Search */}
          <div className="relative w-72 shrink-0">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-4 w-4 text-slate-500" />
            </div>
            <input
              ref={searchInputRef}
              type="text"
              className="block w-full pl-9 pr-12 py-2 text-sm bg-dark-800/60 border border-dark-700/60 rounded-xl text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-primary-500/50 focus:border-primary-500/50 transition-colors"
              placeholder="Search docs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
              {searchTerm ? (
                <button
                  className="pointer-events-auto text-slate-500 hover:text-slate-300"
                  onClick={() => setSearchTerm('')}
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              ) : (
                <kbd className="text-[10px] font-mono text-slate-600 bg-dark-700 px-1.5 py-0.5 rounded">⌘K</kbd>
              )}
            </div>
          </div>
        </div>

        {searchTerm.length >= 3 && (
          <p className="text-xs text-slate-500 mt-2">
            {searchResults.length} section{searchResults.length !== 1 ? 's' : ''} matching "<span className="text-amber-400">{searchTerm}</span>"
          </p>
        )}
      </div>

      {/* Main body: sidebar + content */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Mobile sidebar overlay */}
        {mobileSidebarOpen && (
          <div
            className="fixed inset-0 z-40 bg-dark-900/60 backdrop-blur-sm lg:hidden"
            onClick={() => setMobileSidebarOpen(false)}
          />
        )}

        {/* Sidebar */}
        <nav className={clsx(
          'shrink-0 w-72 border-r border-dark-700/40 bg-dark-800/30 overflow-y-auto',
          'transition-transform duration-200 ease-out',
          // Mobile: overlay
          mobileSidebarOpen ? 'fixed inset-y-0 left-0 z-50 translate-x-0 lg:relative lg:translate-x-0' : 'hidden lg:block',
        )}>
          <div className="p-3 space-y-0.5">
            {helpSections.map((section) => {
              const Icon = section.icon
              const isActive = activeSection === section.id
              const isFiltered = searchResults.length > 0 && !searchResults.includes(section.id)

              return (
                <button
                  key={section.id}
                  onClick={() => handleSectionChange(section.id)}
                  className={clsx(
                    'w-full text-left px-3 py-2.5 rounded-xl text-sm transition-all duration-150 group',
                    isActive
                      ? 'bg-primary-500/10 text-primary-400 border border-primary-500/20'
                      : 'text-slate-400 hover:text-slate-200 hover:bg-dark-700/50 border border-transparent',
                    isFiltered && 'opacity-30',
                  )}
                >
                  <div className="flex items-center gap-2.5">
                    <Icon className={clsx('h-4 w-4 shrink-0', isActive ? 'text-primary-400' : 'text-slate-500 group-hover:text-slate-400')} />
                    <div className="min-w-0">
                      <div className="font-medium truncate text-[13px]">{section.title}</div>
                      <div className={clsx('text-[11px] mt-0.5 truncate', isActive ? 'text-primary-500/70' : 'text-slate-600')}>
                        {section.description}
                      </div>
                    </div>
                  </div>
                </button>
              )
            })}
          </div>
        </nav>

        {/* Content area */}
        <div ref={contentRef} className="flex-1 overflow-y-auto">
          <div className="max-w-4xl mx-auto px-6 py-6">
            {activeContent && (
              <>
                {/* Section header */}
                <div className="mb-8">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl bg-primary-500/10 border border-primary-500/20 flex items-center justify-center">
                      <activeContent.icon className="h-5 w-5 text-primary-400" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-text-primary tracking-tight">{activeContent.title}</h2>
                      <p className="text-sm text-slate-500">{activeContent.description}</p>
                    </div>
                  </div>

                  {/* "On this page" quick links */}
                  {headings.length > 2 && (
                    <div className="mt-4 p-3 rounded-xl bg-dark-800/40 border border-dark-700/30">
                      <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2">On this page</p>
                      <div className="flex flex-wrap gap-x-4 gap-y-1">
                        {headings.map(h => (
                          <a
                            key={h.id}
                            href={`#${h.id}`}
                            className="text-xs text-slate-400 hover:text-primary-400 transition-colors flex items-center gap-1"
                            onClick={(e) => {
                              e.preventDefault()
                              document.getElementById(h.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                            }}
                          >
                            <ChevronRightIcon className="h-3 w-3 text-slate-600" />
                            {h.text}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Content blocks */}
                <div className="space-y-4">
                  {activeContent.content.map((block, i) => (
                    <ContentRenderer key={i} block={block} searchQuery={searchTerm} />
                  ))}
                </div>

                {/* Footer navigation */}
                <div className="mt-12 pt-6 border-t border-dark-700/30 flex items-center justify-between">
                  {(() => {
                    const idx = helpSections.findIndex(s => s.id === activeSection)
                    const prev = idx > 0 ? helpSections[idx - 1] : null
                    const next = idx < helpSections.length - 1 ? helpSections[idx + 1] : null
                    return (
                      <>
                        {prev ? (
                          <button
                            onClick={() => handleSectionChange(prev.id)}
                            className="text-sm text-slate-400 hover:text-primary-400 transition-colors flex items-center gap-2"
                          >
                            <ChevronRightIcon className="h-4 w-4 rotate-180" />
                            {prev.title}
                          </button>
                        ) : <div />}
                        {next ? (
                          <button
                            onClick={() => handleSectionChange(next.id)}
                            className="text-sm text-slate-400 hover:text-primary-400 transition-colors flex items-center gap-2"
                          >
                            {next.title}
                            <ChevronRightIcon className="h-4 w-4" />
                          </button>
                        ) : <div />}
                      </>
                    )
                  })()}
                </div>

                {/* Contact bar */}
                <div className="mt-6 flex flex-col sm:flex-row gap-3">
                  <div className="flex-1 p-4 rounded-xl bg-primary-500/5 border border-primary-500/15 flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-300 font-medium">Still need help?</p>
                      <p className="text-xs text-slate-500 mt-0.5">Contact your administrator or reach out to support</p>
                    </div>
                    <a
                      href="mailto:guardian-support@iuvotech.com"
                      className="inline-flex items-center gap-2 px-4 py-2 text-xs font-medium text-primary-400 bg-primary-500/10 hover:bg-primary-500/20 border border-primary-500/20 rounded-lg transition-colors"
                    >
                      Email Support
                      <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                    </a>
                  </div>
                  <div className="flex-1 p-4 rounded-xl bg-amber-500/5 border border-amber-500/15 flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-300 font-medium">Feature request or bug?</p>
                      <p className="text-xs text-slate-500 mt-0.5">Submit feedback to help us improve Guardian</p>
                    </div>
                    <a
                      href="https://github.com/iuvonauts/guardian-feedback/issues"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 text-xs font-medium text-amber-400 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/20 rounded-lg transition-colors"
                    >
                      Send Feedback
                      <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                    </a>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
